<template>
  <LoadingContainer :is-loading="isLoading">
    <div class="card">
      <div class="card-header">
        <div class="card-header-title">
          <div class="title is-6 is-marginless">
            Κάρτες λίστας
            <span class="tag ml-2">{{ items.length }}</span>
          </div>
        </div>

        <div class="card-header-icon">
          <div class="buttons is-marginless are-small">
            <RouterLink
              :to="{
                name: 'content.home.card-link-lists.edit',
                params: { id: listId },
              }"
              class="button"
            >
              <span class="icon is-small"><i class="fa fa-chevron-left"/></span>
              <span>Πίσω στo Card Link</span>
            </RouterLink>

            <RouterLink
              :to="{
                name: 'content.home.card-link-lists.items.add',
                params: { id: listId },
              }"
              class="button"
            >
              <span class="icon is-small"><i class="fa fa-plus"/></span>
              <span>Προσθήκη</span>
            </RouterLink>
          </div>
        </div>
      </div>

      <div class="card-content">
        <div class="columns">
          <div class="column is-full">
            <div class="table-wrapper">
              <table
                v-if="items.length"
                class="table is-bordered is-striped is-marginless is-fullwidth"
              >
                <thead>
                  <tr>
                    <th class="has-text-centered" />
                    <th class="has-text-centered">#</th>
                    <th>Εικόνα</th>
                    <th>Τίτλος (EL)</th>
                    <th>Τίτλος (EN)</th>
                    <th>Link</th>
                    <th class="has-text-centered">Εμφανής</th>
                    <th class="has-text-centered">Ημ/νια Δημ.</th>
                    <th class="has-text-centered">Ημ/νια Ενημ.</th>
                    <th class="hidden-print">Ενέργειες</th>
                  </tr>
                </thead>

                <draggable
                  v-model="items"
                  tag="tbody"
                  handle=".drag-handle"
                  @change="onReorder"
                >
                  <tr v-for="(item, idx) in items" :key="item.id">
                    <!-- Drag handle -->
                    <td class="has-text-centered">
                      <button
                        v-tooltip="'Σύρετε για αναδιάταξη'"
                        class="button is-small is-white drag-handle"
                        type="button"
                      >
                        <span class="icon is-small">
                          <i class="fa fa-bars" />
                        </span>
                      </button>
                    </td>

                    <!-- Index -->
                    <td class="has-text-centered">{{ idx + 1 }}</td>

                    <!-- Thumbnail -->
                    <td style="width:80px">
                      <figure v-if="item.image_url" class="image">
                        <img :src="item.image_url" alt="thumb" />
                      </figure>
                      <span v-else>—</span>
                    </td>

                    <!-- Titles -->
                    <td>
                      <RouterLink
                        :to="{
                          name: 'content.home.card-link-lists.items.edit',
                          params: { id: listId, itemId: item.id },
                        }"
                      >
                        {{ item.title_el }}
                      </RouterLink>
                    </td>
                    <td>{{ item.title_en }}</td>

                    <!-- Link -->
                    <td class="link-cell">
                      <a
                        :href="item.link"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {{ item.link }}
                      </a>
                    </td>

                    <!-- Active toggle -->
                    <td class="has-text-centered">
                      <ToggleButton
                        :value="!!item.is_active"
                        :sync="true"
                        :color="{ checked: '#22A684', unchecked: '#ff3860' }"
                        :width="30"
                        :height="15"
                        @change="toggleActive(item, $event)"
                      />
                      <p class="help is-size-7 mt-1">Μέχρι 4 ενεργές κάρτες</p>
                    </td>

                    <!-- Dates -->
                    <td class="has-text-centered">
                      {{ formatDateTime(item.created_at) }}
                    </td>
                    <td class="has-text-centered">
                      {{ formatDateTime(item.updated_at) }}
                    </td>

                    <!-- Actions -->
                    <td class="hidden-print actions-cell has-text-centered">
                      <div class="buttons are-small actions-buttons">
                        <RouterLink
                          :to="{
                            name: 'content.home.card-link-lists.items.edit',
                            params: { id: listId, itemId: item.id },
                          }"
                          class="button is-small"
                          title="Επεξεργασία"
                        >
                          <span class="icon is-small">
                            <i class="fa fa-pencil" />
                          </span>
                        </RouterLink>

                        <button
                          class="button is-small is-outlined"
                          type="button"
                          title="Διαγραφή"
                          @click="askToDelete(item)"
                        >
                          <span class="icon is-small">
                            <i class="fa fa-trash" />
                          </span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </draggable>
              </table>

              <h4 v-else class="title is-4 has-text-centered">
                Δε βρέθηκαν στοιχεία
              </h4>
            </div>
          </div>
        </div>
      </div>

      <!-- Delete confirmation -->
      <Confirmation
        :model-name="selected ? selected.title_el : ''"
        :is-open="isConfirmOpen"
        description="Είστε βέβαιος για τη διαγραφή;"
        @closeModal="onConfirmClose"
      />
    </div>
  </LoadingContainer>
</template>

<script>
import draggable from 'vuedraggable';
import request from '@/utils/request';
import LoadingContainer from '@/views/components/LoadingContainer';
import Confirmation from '@/views/components/Confirmation';

export default {
  name: 'CardLinkItemsList',
  components: { draggable, LoadingContainer, Confirmation },

  props: {
    id: { type: [String, Number], required: true }, // from route
  },

  data: () => ({
    isLoading: false,
    items: [],
    originalSnapshot: [],
    isConfirmOpen: false,
    selected: null,
  }),

  computed: {
    listId() {
      return Number(this.id);
    },
  },

  metaInfo: { title: 'Card Link Items' },

  watch: {
    id: {
      immediate: true,
      handler() {
        this.fetch();
      },
    },
  },

  methods: {
    async fetch() {
      try {
        this.isLoading = true;
        const { data } = await request.get(
          `/home/card-link-lists/${this.listId}/items`,
        );
        const list = Array.isArray(data?.items) ? data.items : [];
        // ensure numeric order sort
        this.items = list
          .slice()
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        this.snapshot();
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'To αίτημα απέτυχε',
        });
      } finally {
        this.isLoading = false;
      }
    },

    snapshot() {
      this.originalSnapshot = JSON.parse(JSON.stringify(this.items));
    },

    async onReorder() {
      // Backend requires ALL items with contiguous order starting at 0
      const payload = this.items.map((it, idx) => ({ id: it.id, order: idx }));
      try {
        await request.patch(
          `/home/card-link-lists/${this.listId}/items/reorder`,
          { items: payload },
        );
        this.items.forEach((it, idx) => this.$set(it, 'order', idx));
        this.snapshot();
        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η αναδιάταξη ολοκληρώθηκε',
        });
      } catch (e) {
        // rollback
        this.items = JSON.parse(JSON.stringify(this.originalSnapshot));
        const msg =
          e?.response?.data?.message || e?.message || 'To αίτημα απέτυχε';
        this.$notify({ type: 'error', title: 'Αποτυχία', text: msg });
      }
    },

    async toggleActive(item, evt) {
      const next = evt?.value !== undefined ? !!evt.value : !item.is_active;
      const prev = !!item.is_active;

      this.$set(item, 'is_active', next);

      try {
        await request.patch(
          `/home/card-link-lists/${this.listId}/items/${item.id}`,
          { is_active: next },
        );
        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: next
            ? 'Η κάρτα είναι τώρα εμφανής.'
            : 'Η κάρτα δεν είναι πλέον εμφανής.',
        });
      } catch (e) {
        // rollback on failure
        this.$set(item, 'is_active', prev);
        const msg =
          e?.response?.data?.message || e?.message || 'To αίτημα απέτυχε';
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: msg.includes('up to 4') ? 'Μέγιστο 4 ενεργές κάρτες' : msg,
        });
      }
    },

    askToDelete(item) {
      this.selected = item;
      this.isConfirmOpen = true;
    },

    async onConfirmClose(confirmed) {
      this.isConfirmOpen = false;
      if (!confirmed || !this.selected) return;

      try {
        const { data } = await request.delete(
          `/home/card-link-lists/${this.listId}/items/${this.selected.id}`,
        );
        if (data?.error) throw new Error(data.message || 'Server error');
        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η κάρτα διαγράφηκε',
        });
        await this.fetch();
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'To αίτημα απέτυχε',
        });
      } finally {
        this.selected = null;
      }
    },

    formatDateTime(v) {
      if (!v) return '-';
      const s = String(v).trim();
      const parts = s.replace('T', ' ').split(' ');
      if (parts.length) {
        const [y, m, d] = (parts[0] || '').split('-');
        const timePart = (parts[1] || '').slice(0, 5);
        if (y && m && d) {
          const pad = n => String(n).padStart(2, '0');
          return `${pad(d)}-${pad(m)}-${y} ${timePart || '00:00'}`;
        }
      }
      const ts = Date.parse(s);
      if (!Number.isNaN(ts)) {
        const dt = new Date(ts);
        const pad = n => String(n).padStart(2, '0');
        return `${pad(dt.getDate())}-${pad(
          dt.getMonth() + 1,
        )}-${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
      }
      return s;
    },
  },
};
</script>

<style scoped>
.table-wrapper {
  position: relative;
}

.drag-handle {
  cursor: move;
}

.image img {
  object-fit: cover;
}

.actions-cell {
  width: 140px;
  text-align: right;
  vertical-align: middle;
}

.actions-buttons {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
}

.actions-buttons .button {
  margin: 0;
  min-width: 34px;
  justify-content: center;
  padding-left: 10px;
  padding-right: 10px;
}

.link-cell {
  max-width: 420px;
  white-space: normal;
}

.link-cell a {
  display: inline-block;
  overflow-wrap: anywhere;
  word-break: break-word;
}
</style>
